import store from '../../../store'
import { PageArea, PageProps } from '../index'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePageState } from './hooks/pageState'
import { DateTerm } from '../../../utils/date'

import { InputError } from '../../containers/BulkSheetView/lib/validation'
import {
  MessageLevel,
  addGlobalMessage,
  addScreenMessage,
} from '../../../store/messages'
import { intl } from '../../../i18n'
import { getRowNumber } from '../../containers/BulkSheetView/lib/gridApi'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useTaskActualWorkTaskDialogState } from './hooks/taskActualWorkTaskDialogState'
import { useAlertDialogState } from './hooks/useAlertDialogState'
import { RouteComponentProps } from 'react-router'
import ActualWorkingHoursView from './components/view'
import { ToolbarToggleValue } from '../../components/toolbars/Toolbar/ToolbarToggle'
import TaskActualWorkApproveHeader from './components/Header'
import { useProjectPrivateContext } from '../../context/projectContext'
import AlertDialog from '../../components/dialogs/AlertDialog'
import {
  ApproveActualWorkingResponse,
  SentBackActualWorkResponse,
  StandardWorkingInfo,
  TaskActualWorkRow,
} from '../ActualWorkingHours/ActualWorkingHours'
import {
  actualWorkingHoursGridOptions,
  bulkApproveCheck,
  bulkSentBackCheck,
} from './gridOptions'
import { useExcelExport } from './hooks/excelExport'
import { useTaskActualWorkData } from './hooks/taskActualWorkData'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { SplitButtonOptions } from '../../components/buttons/SplitButton'
import { useAssignedProjectsData } from '../ActualWorkingHours/hooks/assignedProjectsData'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import FilterPanel from './components/FilterPanel'
import {
  FilterInfo,
  createDefaultFilterInfo,
} from './components/FilterPanel/hooks/useFilterState'
import { TaskActualWorkApproveUserDto } from '../../../applications/ports/taskActualWorkApproveRepository'
import { useFilterPanel } from './hooks/useFilterPanel'

type PathProps = {
  code?: string
  term?: string
  userUuid?: string
}

type Props = PageProps & RouteComponentProps<PathProps>

const ActualWorkingHours = (props: Props) => {
  const functionUuid = props.uuid
  const { project } = useProjectPrivateContext()
  const [loading, setLoading] = useState<boolean>(true)
  const [openCancel, setOpenCancel] = useState<boolean>(false)
  const filterPanel = useFilterPanel()
  const [filterInfo, setFilterInfo] = useState<FilterInfo>(
    createDefaultFilterInfo()
  )
  const [filteredUserList, setFilteredUserList] = useState<
    TaskActualWorkApproveUserDto[]
  >([])
  const [toolbar, setToolbar] = useState<ToolbarToggleValue | undefined>(
    undefined
  )
  const [reloadHeader, setReloadHeader] = useState<boolean>(false)
  const [standardWorkInfo, setStandardWorkInfo] = useState<StandardWorkingInfo>(
    {
      standardWorkDays: 0,
      standardWorkHours: 0,
      totalWorkDays: 0,
      totalWorkHours: 0,
      diffDays: 0,
      diffHours: 0,
      diffDaysPercent: 0,
      diffHoursPercent: 0,
    }
  )
  const {
    data,
    summaryRow,
    ratioRow,
    refresh,
    saveRequest,
    getStandardWorkingInfo,
    hasNoRequestToApproveOrSendBack,
  } = useTaskActualWorkData(project.uuid)
  const { pageState, dateState, userState, dataState } = usePageState({
    functionUuid,
    location: props.location,
  })

  const { initialized, rowHeight, setRowHeight } = pageState
  const { displayDateTerm, setDisplayDateTerm } = dateState
  const { userUuid, userName, onPagingUser, setUserByUuid } = userState
  const { allUsersWithStatus, refreshUsers } = dataState

  // フィルタリング結果に基づくtotalCountとcurrentIndex
  const filteredTotalCount = filteredUserList.length
  const filteredCurrentIndex = useMemo(() => {
    if (!userUuid || filteredUserList.length === 0) return 0
    const index = filteredUserList.findIndex(
      u => u.projectMemberDetail.user.uuid === userUuid
    )
    return index >= 0 ? index + 1 : 0
  }, [userUuid, filteredUserList])

  const { taskDialogState, setTaskDialogState, openTaskActualWorkList } =
    useTaskActualWorkTaskDialogState()
  const { assignedProjects } = useAssignedProjectsData({
    userUuid,
    projectUuid: undefined,
  })
  const { alertDialogState, handleSubmit, handleClose } = useAlertDialogState()

  const callbackCellEdit = useCallback(() => {
    setReloadHeader(true)
  }, [])

  const gridOptions = useMemo(() => {
    return actualWorkingHoursGridOptions({
      editable: true,
      openTaskActualWorkList,
      callbackCellEdit,
    })
  }, [callbackCellEdit, openTaskActualWorkList])

  const submit = useRef<Function>()
  submit.current = saveRequest
  const reload = useRef<Function>()
  reload.current = refresh

  useEffect(() => {
    if (!initialized) return
    refresh(
      userUuid!,
      project.uuid,
      displayDateTerm.startDate!,
      displayDateTerm.endDate!
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userUuid,
    project?.uuid,
    displayDateTerm.startDate,
    displayDateTerm.endDate,
    refresh,
  ])

  const prevData = useRef<TaskActualWorkRow[]>([])
  prevData.current = data
  const hasChanged = useCallback((): boolean => {
    return (prevData.current || data).some(v => v.added || v.edited)
  }, [data])

  useEffect(() => {
    if (!initialized) return
    setLoading(false)
  }, [initialized])

  const refreshAll = useCallback(async () => {
    if (!gridOptions.api || !initialized) return
    setLoading(true)
    try {
      const refreshRows = reload.current ?? refresh
      await refreshRows(
        userUuid,
        project?.uuid,
        displayDateTerm.startDate,
        displayDateTerm.endDate
      )
    } finally {
      gridOptions.context = {
        ...gridOptions.context,
        errors: new InputError(),
      }
      setReloadHeader(true)
      setLoading(false)
    }
  }, [
    gridOptions,
    initialized,
    refresh,
    userUuid,
    project?.uuid,
    displayDateTerm.startDate,
    displayDateTerm.endDate,
  ])

  const handleFailedRequests = useCallback(
    async (
      responses: (ApproveActualWorkingResponse | SentBackActualWorkResponse)[]
    ) => {
      if (!responses || responses.length === 0) {
        return false
      }

      const alreadyApprovedUuids: string[] = []
      const alreadySentBackUuids: string[] = []
      const alreadyCancelledUuids: string[] = []

      responses.forEach(response => {
        if (response.failedRequestStatusUuids) {
          if (response.failedRequestStatusUuids.alreadyApprovedUuid) {
            alreadyApprovedUuids.push(
              response.failedRequestStatusUuids.alreadyApprovedUuid
            )
          }
          if (response.failedRequestStatusUuids.alreadySentBackUuid) {
            alreadySentBackUuids.push(
              response.failedRequestStatusUuids.alreadySentBackUuid
            )
          }
          if (response.failedRequestStatusUuids.alreadyCancelledUuid) {
            alreadyCancelledUuids.push(
              response.failedRequestStatusUuids.alreadyCancelledUuid
            )
          }
        }
      })

      const createFailureDetails = (uuids: string[]) => {
        const matchedData = data.filter(row =>
          row.projectRequestStatus?.some(
            status => status.requestUuid && uuids.includes(status.requestUuid)
          )
        )
        return matchedData
          .map(row => ({
            date: row.date,
            projects:
              row.projectRequestStatus
                ?.filter(
                  status =>
                    status.requestUuid && uuids.includes(status.requestUuid)
                )
                .map(
                  status =>
                    assignedProjects?.find(p => p.uuid === status.projectUuid)
                      ?.displayName
                )
                .filter(Boolean) || [],
          }))
          .filter(data => data.date && data.projects.length > 0)
          .map(data => {
            const [year, month, day] = data.date!.split('-').map(Number)
            return intl.formatMessage(
              { id: `taskActualWork.snackBar.message.failureDetails` },
              { month, day }
            )
          })
          .join('\t')
      }

      let messageText = intl.formatMessage({
        id: `taskActualWork.snackBar.message`,
      })

      if (alreadyApprovedUuids.length > 0) {
        const details = createFailureDetails(alreadyApprovedUuids)
        if (details) {
          messageText +=
            '\n\n' +
            intl.formatMessage({
              id: `taskActualWork.snackBar.message.alreadyApproved`,
            })
          messageText += '\n' + details
        }
      }

      if (alreadySentBackUuids.length > 0) {
        const details = createFailureDetails(alreadySentBackUuids)
        if (details) {
          messageText +=
            '\n\n' +
            intl.formatMessage({
              id: `taskActualWork.snackBar.message.alreadySentBack`,
            })
          messageText += '\n' + details
        }
      }

      if (alreadyCancelledUuids.length > 0) {
        const details = createFailureDetails(alreadyCancelledUuids)
        if (details) {
          messageText +=
            '\n\n' +
            intl.formatMessage({
              id: `taskActualWork.snackBar.message.alreadyCancelled`,
            })
          messageText += '\n' + details
        }
      }

      store.dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'global.warning.businessError' }),
          text: messageText,
        })
      )
      await refreshAll()
      await refreshUsers()
      return true
    },
    [data, assignedProjects, refreshAll, refreshUsers]
  )

  const onSubmit = useCallback(async () => {
    setLoading(true)
    try {
      gridOptions.api?.stopEditing()
      if (gridOptions.context.errors.hasMessage()) {
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: gridOptions.context.errors.toMessage(id => {
              const node = gridOptions.api?.getRowNode(id)
              return !!node ? getRowNumber(node).toString() : ''
            }),
          })
        )
        return
      }
      const response = submit.current
        ? await submit.current()
        : await saveRequest()

      const allFailedResponses: (
        | ApproveActualWorkingResponse
        | SentBackActualWorkResponse
      )[] = []

      if (
        response.json?.approve &&
        Array.isArray(response.json.approve) &&
        response.json.approve.length > 0
      ) {
        const approveFailedResponses = response.json.approve.filter(
          approve => approve.failedRequestStatusUuids
        )
        allFailedResponses.push(...approveFailedResponses)
      }

      if (
        response.json?.sentBack &&
        Array.isArray(response.json.sentBack) &&
        response.json.sentBack.length > 0
      ) {
        const sentBackFailedResponses = response.json.sentBack.filter(
          sentBack => sentBack.failedRequestStatusUuids
        )
        allFailedResponses.push(...sentBackFailedResponses)
      }

      if (allFailedResponses.length > 0) {
        const hasFailures = await handleFailedRequests(allFailedResponses)
        if (hasFailures) return
      }

      if (!response.hasError && !response.hasWarning) {
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        await refreshAll()
        await refreshUsers()
      }
    } finally {
      setLoading(false)
    }
  }, [
    functionUuid,
    gridOptions.api,
    gridOptions.context.errors,
    handleFailedRequests,
    refreshAll,
    refreshUsers,
    saveRequest,
  ])

  const onReload = useCallback(() => {
    if (hasChanged()) {
      setOpenCancel(true)
    } else {
      refreshAll()
    }
  }, [hasChanged, refreshAll])

  const callbackReload = useCallback(async () => {
    await refreshAll()
  }, [refreshAll])

  const onSearch = useCallback(
    async (term: DateTerm) => {
      if (term !== displayDateTerm) {
        setDisplayDateTerm(term)
      }
    },
    [displayDateTerm, setDisplayDateTerm]
  )

  const onExportExcel = useExcelExport(gridOptions, {
    dateTerm: displayDateTerm,
    userName,
    standardInfo: standardWorkInfo,
  })

  const onChangeToolbar = useCallback(
    (value: ToolbarToggleValue | undefined) => {
      setToolbar(value)
    },
    []
  )

  useKeyBind(
    [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
    [data, saveRequest, displayDateTerm]
  )

  useEffect(() => {
    gridOptions.context = {
      ...gridOptions.context,
      errors: new InputError(),
      projectUuid: project?.uuid,
      projectCode: project?.code,
    }
  }, [gridOptions, project?.uuid, project?.code])

  useEffect(() => {
    if (
      !initialized ||
      !displayDateTerm ||
      !displayDateTerm.startDate ||
      !displayDateTerm.endDate ||
      !userUuid
    ) {
      return
    }
    refreshAll()
  }, [initialized, displayDateTerm, userUuid, refreshAll])

  useEffect(() => {
    gridOptions.context = {
      ...gridOptions.context,
      userUuid,
    }
  }, [gridOptions, userUuid])

  useEffect(() => {
    if (reloadHeader) {
      const stdInfo = getStandardWorkingInfo()
      setReloadHeader(false)
      setStandardWorkInfo(stdInfo)
    }
  }, [getStandardWorkingInfo, reloadHeader])

  useEffect(() => {
    gridOptions.api?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowHeight])

  const onCheckApproveAll = useCallback(() => {
    bulkApproveCheck(data, gridOptions.api)
  }, [data, gridOptions.api])

  const onCheckSentBackAll = useCallback(() => {
    bulkSentBackCheck(data, gridOptions.api)
  }, [data, gridOptions.api])

  const handleUserSelect = useCallback(
    (user: TaskActualWorkApproveUserDto) => {
      setUserByUuid(user.projectMemberDetail.user.uuid)
      filterPanel.close()
    },
    [setUserByUuid, filterPanel.close]
  )

  const handleFilterChange = useCallback((info: FilterInfo) => {
    setFilterInfo(info)
  }, [])

  const handleFilteredUsersChange = useCallback(
    (users: TaskActualWorkApproveUserDto[]) => {
      setFilteredUserList(users)
    },
    []
  )

  useEffect(() => {
    if (!initialized || filteredUserList.length === 0) return

    const currentUserExists = filteredUserList.some(
      u => u.projectMemberDetail.user.uuid === userUuid
    )

    if (!currentUserExists && filteredUserList.length > 0) {
      setUserByUuid(filteredUserList[0].projectMemberDetail.user.uuid)
    }
  }, [filteredUserList, userUuid, initialized, setUserByUuid])

  const handlePagingUser = useCallback(
    (index: number) => {
      const targetUser = filteredUserList[index - 1]
      if (targetUser) {
        setUserByUuid(targetUser.projectMemberDetail.user.uuid)
      }
    },
    [filteredUserList, setUserByUuid]
  )

  const handleReloadUsers = useCallback(async () => {
    setLoading(true)
    try {
      await refreshUsers()
    } finally {
      setLoading(false)
    }
  }, [refreshUsers])

  const batchMenu = useMemo(() => {
    return [
      {
        key: intl.formatMessage({ id: 'taskActualWork.approveCheckAll' }),
        onClick: onCheckApproveAll,
      },
      {
        key: intl.formatMessage({ id: 'taskActualWork.sentBackCheckAll' }),
        onClick: onCheckSentBackAll,
      },
    ] as SplitButtonOptions[]
  }, [onCheckApproveAll, onCheckSentBackAll])

  return (
    <>
      <AlertDialog
        isOpen={alertDialogState?.isOpen || false}
        title={alertDialogState?.title}
        message={alertDialogState?.message}
        submitHandler={handleSubmit}
        closeHandler={handleClose}
      />
      <PageArea>
        <SavePopper loading={loading} onSubmit={onSubmit} />
        <TaskActualWorkApproveHeader
          loading={loading}
          onSubmit={onSubmit}
          onCancel={onReload}
          dateTerm={displayDateTerm}
          onSearch={onSearch}
          onClickExport={onExportExcel}
          toolbar={toolbar}
          onChangeToolbar={onChangeToolbar}
          rowHeight={rowHeight}
          onChangeRowHeight={(value: number) => setRowHeight(value)}
          userName={userName}
          currentIndex={filteredCurrentIndex}
          totalCount={filteredTotalCount}
          onPagingUser={handlePagingUser}
          filterPanel={filterPanel}
          splitButtonProps={{
            options: batchMenu,
            disabled: hasNoRequestToApproveOrSendBack,
          }}
        />
        <ActualWorkingHoursView
          gridOptions={gridOptions}
          loading={loading}
          userUuid={userUuid}
          data={data}
          summaryRow={summaryRow}
          ratioRow={ratioRow}
          rowHeight={rowHeight}
          onCallbackReload={callbackReload}
          taskDialogState={taskDialogState}
          setTaskDialogState={setTaskDialogState}
          openTaskActualWorkList={openTaskActualWorkList}
          openCancel={openCancel}
          setOpenCancel={setOpenCancel}
        />
        <FilterPanel
          open={filterPanel.isOpen}
          onClose={filterPanel.close}
          allUsersWithStatus={allUsersWithStatus}
          onUserSelect={handleUserSelect}
          onReload={handleReloadUsers}
          onFilterChange={handleFilterChange}
          onFilteredUsersChange={handleFilteredUsersChange}
        />
      </PageArea>
    </>
  )
}

export default projectPrivate(ActualWorkingHours)
